'use client';

import {
  Children,
  type PointerEvent,
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';

interface SeoContentCarouselLabels {
  next: string;
  previous: string;
  slide: string;
}

interface SeoContentCarouselProps {
  children: ReactNode;
  labels: SeoContentCarouselLabels;
}

const CAROUSEL_CHEVRON_PATH = '/zeelool-next/goods-list/seo-footer/carousel-chevron.svg';
const AUTOPLAY_DELAY = 5000;
const DRAG_THRESHOLD = 40;

export default function SeoContentCarousel({ children, labels }: SeoContentCarouselProps) {
  const slides = Children.toArray(children);
  const carouselRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseRef = useRef({ hover: false, focus: false, pointer: false });
  const dragRef = useRef<{ pointerId: number; startX: number; scrollLeft: number } | null>(null);
  const lastIndex = slides.length - 1;

  const scrollToSlide = useCallback(
    (index: number) => {
      const viewport = viewportRef.current;
      if (!viewport) return;

      const nextIndex = Math.min(Math.max(index, 0), lastIndex);
      viewport.scrollTo({
        left: nextIndex * viewport.clientWidth,
      });
    },
    [lastIndex],
  );

  const restartAutoplay = useCallback(
    function schedule() {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
      const pause = pauseRef.current;
      if (lastIndex < 1 || document.hidden || pause.hover || pause.focus || pause.pointer) return;

      timerRef.current = setTimeout(() => {
        const viewport = viewportRef.current;
        if (viewport?.clientWidth) {
          const index = Math.round(viewport.scrollLeft / viewport.clientWidth);
          scrollToSlide((index + 1) % (lastIndex + 1));
        }
        schedule();
      }, AUTOPLAY_DELAY);
    },
    [lastIndex, scrollToSlide],
  );

  useEffect(() => {
    // 首尾箭头卸载不一定触发 blur，切页后以真实焦点恢复自动播放。
    pauseRef.current.focus = Boolean(carouselRef.current?.contains(document.activeElement));
    restartAutoplay();
    document.addEventListener('visibilitychange', restartAutoplay);
    return () => {
      document.removeEventListener('visibilitychange', restartAutoplay);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [activeIndex, restartAutoplay]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const viewport = event.currentTarget;
    if (!viewport.clientWidth) return;

    const nextIndex = Math.min(
      Math.max(Math.round(viewport.scrollLeft / viewport.clientWidth), 0),
      lastIndex,
    );
    setActiveIndex(nextIndex);
    restartAutoplay();
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (lastIndex < 1 || event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('a[href]')) return;
    pauseRef.current.pointer = true;
    restartAutoplay();
    // 触屏保留原生横向滑动与纵向页面滚动，仅补充鼠标拖动。
    if (event.pointerType !== 'mouse') return;
    const viewport = event.currentTarget;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: viewport.scrollLeft,
    };
    viewport.dataset.dragging = 'true';
    viewport.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.currentTarget.scrollLeft = drag.scrollLeft + drag.startX - event.clientX;
  };

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const viewport = event.currentTarget;
    if (drag && drag.pointerId === event.pointerId) {
      dragRef.current = null;
      delete viewport.dataset.dragging;
      if (viewport.hasPointerCapture?.(event.pointerId))
        viewport.releasePointerCapture(event.pointerId);
      if (viewport.clientWidth) {
        const distance = drag.startX - event.clientX;
        const startIndex = Math.round(drag.scrollLeft / viewport.clientWidth);
        const nextIndex =
          event.type === 'pointerup' && Math.abs(distance) >= DRAG_THRESHOLD
            ? startIndex + Math.sign(distance)
            : Math.round(viewport.scrollLeft / viewport.clientWidth);
        scrollToSlide(nextIndex);
      }
    }
    pauseRef.current.pointer = false;
    restartAutoplay();
  };

  if (slides.length === 0) return null;

  return (
    <div
      ref={carouselRef}
      role="group"
      className="flex w-full flex-col gap-[19px] min-[920px]:gap-[24px]"
      onPointerEnter={(event) => {
        if (event.pointerType !== 'mouse') return;
        pauseRef.current.hover = true;
        restartAutoplay();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'mouse') return;
        pauseRef.current.hover = false;
        restartAutoplay();
      }}
      onFocusCapture={() => {
        pauseRef.current.focus = true;
        restartAutoplay();
      }}
      onBlurCapture={(event) => {
        if (
          event.relatedTarget instanceof Node &&
          event.currentTarget.contains(event.relatedTarget)
        )
          return;
        pauseRef.current.focus = false;
        restartAutoplay();
      }}
    >
      <div
        data-seo-carousel-card
        className="relative min-h-[240px] overflow-hidden rounded-[8px] bg-white min-[920px]:min-h-[203px]"
      >
        <div
          ref={viewportRef}
          data-seo-carousel-viewport
          className="scrollbar-hide flex min-h-[240px] snap-x snap-mandatory items-stretch overflow-x-auto overscroll-x-contain scroll-smooth motion-reduce:scroll-auto data-[dragging=true]:cursor-grabbing data-[dragging=true]:snap-none data-[dragging=true]:select-none data-[dragging=true]:scroll-auto [-webkit-overflow-scrolling:touch] min-[920px]:min-h-[203px] min-[920px]:cursor-grab"
          onScroll={handleScroll}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onLostPointerCapture={handlePointerEnd}
        >
          {slides.map((slide, index) => (
            <div key={index} className="flex w-full min-w-0 shrink-0 snap-start">
              {slide}
            </div>
          ))}
        </div>

        {slides.length > 1 && activeIndex > 0 ? (
          <button
            type="button"
            aria-label={labels.previous}
            className="absolute left-[28px] top-1/2 hidden size-[24px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple min-[920px]:flex"
            onClick={() => {
              scrollToSlide(activeIndex - 1);
              restartAutoplay();
            }}
          >
            <Image
              src={CAROUSEL_CHEVRON_PATH}
              alt=""
              width={15}
              height={15}
              className="rotate-90"
            />
          </button>
        ) : null}

        {slides.length > 1 && activeIndex < lastIndex ? (
          <button
            type="button"
            aria-label={labels.next}
            className="absolute right-[28px] top-1/2 hidden size-[24px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple min-[920px]:flex"
            onClick={() => {
              scrollToSlide(activeIndex + 1);
              restartAutoplay();
            }}
          >
            <Image
              src={CAROUSEL_CHEVRON_PATH}
              alt=""
              width={15}
              height={15}
              className="-rotate-90"
            />
          </button>
        ) : null}
      </div>

      {slides.length > 1 ? (
        <div
          data-seo-carousel-pagination
          className="flex h-[3px] items-center justify-center gap-[8px]"
        >
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-current={activeIndex === index ? 'true' : undefined}
              aria-label={labels.slide.replace('{index}', String(index + 1))}
              className="flex h-[24px] items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple"
              onClick={() => {
                scrollToSlide(index);
                restartAutoplay();
              }}
            >
              <span
                className={`h-[3px] rounded-[99px] transition-[width,background-color] motion-reduce:transition-none ${
                  activeIndex === index
                    ? 'w-[40px] bg-primary-purple'
                    : 'w-[24px] bg-primary-purple-disable'
                }`}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
